import { Images } from '@/assets/images';
import { DocTypeEnum, REGEX } from '@/constant';
import {
	cn,
	dayjs,
	formatDate,
	map,
	removeSuffixWord,
	SUPPORT_FORMAT_DATE_LIST,
} from '@/utils';

import { RangePickerProps } from 'antd/es/date-picker';

import { range } from 'ramda';

import { FormConfig } from '../define-document-write.type';

export const accomInspectionDetailsArr = map(range(0, 11), (item) => ({
	id: item,
	valueInput1: '',
	valueInput2: '',
}));

export type Cachet = {
	cachetId: string;
	cachetContent: string;
	cachetBaseExt: string;
	cachetFileName: string;
};

export const NOT_SUITABLE = [
	{ id: 1, value: '비닐하우스', label: '비닐하우스' },
	{ id: 2, value: '컨테이너', label: '컨테이너' },
	{ id: 3, value: '창고 개조', label: '창고 개조' },
	{ id: 4, value: '기타', label: '기타' },
];

export const ACCOMMODATION = [
	{ id: 1, value: '아파트', label: '아파트' },
	{ id: 2, value: '주택', label: '주택' },
	{ id: 3, value: '회관', label: '회관' },
	{ id: 4, value: '지자체 숙소', label: '지자체 숙소' },
	{ id: 5, value: '기타', label: '기타' },
];

export const EXIST_OPTIONS = [
	{ id: 1, value: '유', label: '유' },
	{ id: 2, value: '무', label: '무' },
];

export const SUITABLE_OPTIONS = [
	{ id: 1, value: '적합', label: '적합' },
	{ id: 2, value: '부적합', label: '부적합' },
];

export const INSTALLATION_OPTIONS = [
	{ id: 1, value: '설치', label: '설치' },
	{ id: 2, value: '미설치', label: '미설치' },
];

export const COUNTY_DISTRICT_SUFFIX = ['시', '군', '구'];

export const DEPARTMENT_NAME_SUFFIX = ['과'];

export const NUMBER_OF_ATTACHMENT = '3';

const initializeValue = {
	//Page 1
	accomExamStartDate: '',
	accomExamEndDate: '',
	accomExamRegistDate: '',
	accomExamSignature: '',
	accomExamSignatureFilePath: '',
	accomExamAttachNum: '',
	accomExamDistrict: '',

	//Page 2
	accomExamInspectorCountyDistrict: '',
	accomExamInspectorPosition: '', // '시‧군',
	accomExamInspectorDepartmentName: '',
	accomExamInspectorAdminName: '',
	accomExamDate: '',
	accomInspectionDetails: '', //json string
	specifics: '',

	accomInspectionDetailsArr: accomInspectionDetailsArr,

	accomInspectionTarget: '',

	//Page 3
	accomBuildingPic: '',
	accomRoomPic: '',
	accomToiletPic: '',
	accomBathroomPic: '',
	accomOtherOnePic: '',
	accomOtherTwoPic: '',

	signature: {} as Cachet,
};

type IValues = typeof initializeValue;

export const ACCOMMODATION_CONFIG: FormConfig<IValues, DocTypeEnum.PM1_10> = {
	initializeValue,
	title: '숙소점검 확인서',
	mapPropToValues({ detailData, lodgingData }) {
		const { adminInfo, unitInfo } = detailData;

		const unitCountyDistrict = unitInfo.countyDistrict;
		const unitCountyDistrictWithoutSuffix = removeSuffixWord(
			unitCountyDistrict,
			COUNTY_DISTRICT_SUFFIX
		);

		const departmentNameWithoutSuffix = removeSuffixWord(
			adminInfo?.departmentName,
			DEPARTMENT_NAME_SUFFIX
		);

		// 7: inspection date
		const inspectionDate = lodgingData?.inspectionDate
			? formatDate(lodgingData.inspectionDate, {
					outputFormat: 'YYYY-MM-DD HH:mm',
				})
			: '';

		// 8: inspection target
		const address = lodgingData?.address || '';
		const providerName = lodgingData?.providerName || '';
		const providerPhone = lodgingData?.providerPhone || '';
		const inspectionTarget = [
			address,
			providerName && providerName,
			providerPhone && `(${providerPhone})`,
		]
			.filter(Boolean)
			.join(', ');

		return {
			accomExamAttachNum: NUMBER_OF_ATTACHMENT, // 2
			accomExamDistrict: unitCountyDistrict,
			accomExamInspectorCountyDistrict: unitCountyDistrictWithoutSuffix,
			accomExamInspectorDepartmentName: departmentNameWithoutSuffix,
			accomExamInspectorAdminName: adminInfo?.name,
			accomExamDate: inspectionDate, // 7
			accomInspectionTarget: inspectionTarget, //8

			accomExamRegistDate: formatDate(dayjs()),
		};
	},
	pages: [
		{
			background: {
				src: Images.formAccommodationPage1Highlight,
				className: 'template',
				dataSource: Images.formAccommodationPage1,
			},
			textAreas: [
				{
					name: 'accomExamAttachNum',
					maxHeight: 28,
					maxLength: 3,
					className: 'text-[19px] text-center',
					containerClassName: 'absolute top-[737px] left-[546px] w-[53px]',
					regex: REGEX.ALLOW_NUMBER,
				},
				(values, index) => ({
					name: 'accomExamDistrict',
					maxHeight: 32,
					maxLength: 20,
					className: 'text-[19px] text-center focus:bg-white !h-[34px]',
					containerClassName: cn(
						'absolute top-[929px] left-[75px] w-[345px]',
						values?.accomExamDistrict && 'bg-white'
					),
					onKeyDown: (e) => {
						if (e.key === 'Tab' || e.key === 'Enter') {
							e.preventDefault();
						}
					},
				}),
			],
			datePickers: [
				(values, index) => {
					const disabledDateStartDate: RangePickerProps['disabledDate'] = (
						current
					) => {
						return (
							!!values.accomExamEndDate &&
							current &&
							current >
								dayjs(values.accomExamEndDate, SUPPORT_FORMAT_DATE_LIST).endOf(
									'day'
								)
						);
					};

					return {
						name: 'accomExamStartDate',
						segmentedInputs: true,
						format: 'YYYY     MM     DD',
						containerClassName: 'absolute top-[343px] left-[115px] w-[148px]',
						disabledDate: disabledDateStartDate,
						style: { fontSize: '19px' },
					};
				},
				(values, index) => {
					const disabledDateEndDate: RangePickerProps['disabledDate'] = (
						current
					) => {
						return (
							!!values.accomExamStartDate &&
							current &&
							current <
								dayjs(values.accomExamStartDate, SUPPORT_FORMAT_DATE_LIST)
						);
					};

					return {
						name: 'accomExamEndDate',
						segmentedInputs: true,
						format: 'YYYY      MM     DD',
						containerClassName: 'absolute top-[343px] left-[318px] w-[148px]',
						disabledDate: disabledDateEndDate,
						style: { fontSize: '19px' },
					};
				},
				{
					name: 'accomExamRegistDate',
					segmentedInputs: true,
					format: 'YYYY            MM             DD',
					containerClassName: 'absolute top-[829px] left-[254px] ',
					style: { fontSize: '19px' },
				},
			],
			cachetForms: [
				{
					className: 'absolute top-[78%] left-[54%] w-[136px] h-[136px]',
				},
			],
		},
		{
			background: {
				src: Images.formAccommodationPage2Highlight,
				className: 'template',
				dataSource: Images.formAccommodationPage2,
			},
			textAreas: [
				(values, index) => ({
					name: 'accomExamInspectorCountyDistrict',
					maxHeight: 46,
					maxLength: 16,
					onKeyDown: (e) => {
						if (e.key === 'Tab' || e.key === 'Enter') {
							e.preventDefault();
						}
					},
					className: 'text-base text-left focus:bg-white',
					containerClassName:
						'absolute items-end top-[184px] left-[215px] w-[130px] z-[1]',
				}),
				(values, index) => ({
					name: 'accomExamInspectorPosition',
					maxHeight: 46,
					maxLength: 4,
					onKeyDown: (e) => {
						if (e.key === 'Tab' || e.key === 'Enter') {
							e.preventDefault();
						}
					},
					className: 'text-base text-center',
					containerClassName: cn(
						'absolute items-end top-[188px] left-[281px] w-[69px]',
						values?.accomExamInspectorCountyDistrict && 'bg-white'
					),
					readOnly: true,
				}),
				{
					name: 'accomExamInspectorDepartmentName',
					maxHeight: 46,
					maxLength: 12,
					onKeyDown: (e) => {
						if (e.key === 'Tab' || e.key === 'Enter') {
							e.preventDefault();
						}
					},
					className: 'text-base text-center',
					containerClassName:
						'absolute items-end top-[184px] left-[347px] w-[90px]',
				},
				{
					name: 'accomExamInspectorAdminName',
					maxHeight: 46,
					maxLength: 28,
					onKeyDown: (e) => {
						if (e.key === 'Tab' || e.key === 'Enter') {
							e.preventDefault();
						}
					},
					className: 'text-base text-center',
					containerClassName:
						'absolute items-end top-[184px] left-[477px] w-[200px]',
				},
				{
					name: 'accomInspectionTarget',
					maxHeight: 44,
					maxLength: 64,
					className: 'text-base',
					containerClassName: 'absolute top-[284px] left-[214px] w-[450px]',
				},
				{
					name: 'specifics',
					maxHeight: 120,
					rows: 5,
					maxLength: 50,
					className: 'text-base',
					containerClassName:
						'absolute top-[88.9%] left-[14%] w-[610px] !items-start',
					regex: REGEX.ALLOW_KR_SPACE,
				},
				(values, index) => ({
					repeat: 11,
					jsonKey: 'accomInspectionDetailsArr',
					name: 'valueInput2',
					maxHeight: 46,
					maxLength: 20,
					'data-tabIndex': index,
					boxStyle: { top: `${431 + 45.5 * index}px` },
					className: '!text-[14px]',
					containerClassName: 'absolute left-[613px] w-[110px]',
					regex: REGEX.ALLOW_KR_SPACE,
				}),
			],
			datePickers: [
				{
					name: 'accomExamDate',
					className:
						'[&>.ant-picker-input>input]:!text-[16px] [&>.ant-picker-input>input]:!w-[272px]',
					format: 'YYYY           MM        DD     HH     mm',
					showTime: { format: 'HH:mm' },
					containerClassName: 'absolute top-[251px] left-[217px]',
					outputFormat: 'YYYY-MM-DD HH:mm',
					style: {
						width: '272px',
						fontSize: '16px',
						// marginTop: '0',
					},
				},
			],
			autocompletes: [
				(values, index) => ({
					index,
					repeat: 11,
					jsonKey: 'accomInspectionDetailsArr',
					name: 'valueInput1',
					options: [
						NOT_SUITABLE, //1
						ACCOMMODATION, //2
						EXIST_OPTIONS, //3
						EXIST_OPTIONS, //4
						EXIST_OPTIONS, //5
						EXIST_OPTIONS, //6
						INSTALLATION_OPTIONS, //7
						SUITABLE_OPTIONS, //8
						SUITABLE_OPTIONS, //9
						EXIST_OPTIONS, //10
						EXIST_OPTIONS, //11
					],
					'data-tabIndex': index,
					boxStyle: { top: `${438 + 45.5 * index}px` },
					className: '!text-[14px]',
					containerClassName: 'absolute left-[438px] w-[166px]',
					maxLength: 14,
				}),
			],
		},
		{
			background: {
				src: Images.formAccommodationPage3Highlight,
				className: 'template',
				dataSource: Images.formAccommodationPage3,
			},
			inputFiles: [
				{
					name: 'accomBuildingPic',
					className: 'absolute top-[169px] left-[89px] w-[300px] h-[240px]',
				},
				{
					name: 'accomRoomPic',
					className: 'absolute top-[169px] left-[405px] w-[300px] h-[240px]',
				},
				{
					name: 'accomToiletPic',
					className: 'absolute top-[459px] left-[89px] w-[300px] h-[240px]',
				},
				{
					name: 'accomBathroomPic',
					className: 'absolute top-[459px] left-[405px] w-[300px] h-[240px]',
				},
				{
					name: 'accomOtherOnePic',
					className: 'absolute top-[751px] left-[89px] w-[300px] h-[240px]',
				},
				{
					name: 'accomOtherTwoPic',
					className: 'absolute top-[751px] left-[405px] w-[300px] h-[240px]',
				},
			],
		},
	],
};
